import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { LoginWithIdentityFormValues } from "../../Models/Auth";
import { sendEvent } from "../../Utils/analytics";
import { getRemoteValue } from "../../Utils/firebase";
import useAuthState from "../../Utils/store";
import CLoadingButton from "../CLoadingButton/CLoadingButton";
import FormProvider from "../HookForm/FormProvider";
import RHFInput from "../HookForm/RHFInput";
import RHFPhoneInput from "../HookForm/RHFPhoneInput";
import { isValidPhone, phoneDigits } from "../Login/phone";

const ContentStyle = styled("div")(({ theme }) => ({
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  maxWidth: "420px",
  background: "#FFFFFF",
  borderRadius: "16px",
  "@media (min-width: 768.5px)": {
    minHeight: "400px",
  },
}));

// Turkish Identity Number Validation Function
const validateTurkishIdentityNo = (identityNo: string): boolean => {
  // Remove any non-numeric characters
  const cleanedNumber = identityNo.replace(/\D/g, "");

  // Check if it's 11 digits long
  if (cleanedNumber.length !== 11) {
    return false;
  }

  // Check if first digit is not 0
  if (cleanedNumber[0] === "0") {
    return false;
  }

  // Convert to array of numbers
  const digits = cleanedNumber.split("").map(Number);

  // Check if all digits are the same (invalid case)
  if (digits.every(digit => digit === digits[0])) {
    return false;
  }

  // Algorithm 1: Sum of first 10 digits modulo 10 should equal the 11th digit
  const sumFirst10 = digits.slice(0, 10).reduce((sum, digit) => sum + digit, 0);
  if (sumFirst10 % 10 !== digits[10]) {
    return false;
  }

  // Algorithm 2: (sum of odd positioned digits * 7 - sum of even positioned digits) % 10 should equal 10th digit
  const sumOddPositions = digits[0] + digits[2] + digits[4] + digits[6] + digits[8];
  const sumEvenPositions = digits[1] + digits[3] + digits[5] + digits[7];
  const checkDigit = (sumOddPositions * 7 - sumEvenPositions) % 10;

  if (checkDigit !== digits[9]) {
    return false;
  }

  return true;
};

const IdentityLogin = () => {
  const { loginUser } = useAuthState();
  const navigate = useNavigate();
  const ValidationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(getRemoteValue("phone_must"))
      .test("phone", getRemoteValue("phone_regex_warning"), (value) =>
        isValidPhone(value ?? "")
      ),
    identityNo: Yup.string()
      .required(getRemoteValue("identity_number_must"))
      .test(
        "turkish-identity",
        getRemoteValue("identity_regex_warning"),
        function (value) {
          if (!value) return false;
          return validateTurkishIdentityNo(value);
        }
      ),
  });

  const methodsSubmit = useForm<LoginWithIdentityFormValues>({
    mode: "onChange",
    resolver: yupResolver(ValidationSchema),
    defaultValues: {
      phoneNumber: "",
      identityNo: "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors, isSubmitting },
    trigger,
  } = methodsSubmit;

  useEffect(() => {
    setIsDisabled(!isValid);
  }, [isValid]);
  const [isDisabled, setIsDisabled] = useState(true);
  const [isPhoneNumberFocused, setIsPhoneNumberFocused] = useState(false);
  const [isIdentityNoFocused, setIsIdentityNoFocused] = useState(false);

  // Function to check if phone number should show error
  const shouldShowPhoneNumberError = (): boolean => {
    return !!errors.phoneNumber && !isPhoneNumberFocused;
  };

  // Function to check if identity number should show error
  const shouldShowIdentityNoError = (): boolean => {
    return !!errors.identityNo && !isIdentityNoFocused;
  };

  // Generic function to check if any field should show error
  const shouldShowFieldError = (fieldName: 'phoneNumber' | 'identityNo'): boolean => {
    if (fieldName === 'phoneNumber') {
      return shouldShowPhoneNumberError();
    } else if (fieldName === 'identityNo') {
      return shouldShowIdentityNoError();
    }
    return false;
  };

  const onSubmit = async (data: LoginWithIdentityFormValues) => {
    const phoneNumberFormatting = phoneDigits(data.phoneNumber);
    const identityNoFormatting = data.identityNo.replace(/\D/g, "");
    debugger
    sendEvent(`click_send_otp_button`, {
      identity_login: true,
      phone_number: phoneNumberFormatting,
      identity_number: identityNoFormatting
    });
    const resData = await loginUser(phoneNumberFormatting, identityNoFormatting);
    if (resData) {
      debugger
      navigate(resData.route, {
        state: {
          phoneNumber: data.phoneNumber,
          identityNo: data.identityNo,
        },
        replace: true,
      });
    }
  };

  const handleWhatsAppClick = () => {
    window.open(`${getRemoteValue("colendi_info_whatsapp")}`, "_blank");
  };

  const handleMailClick = () => {
    window.open(`mailto:${getRemoteValue("colendi_info_mail")}`, "_blank");
  };

  const handleFocus = (fieldName: string) => {
    if (fieldName === "phoneNumber") {
      setIsPhoneNumberFocused(true);
    } else if (fieldName === "identityNo") {
      setIsIdentityNoFocused(true);
    }
    methodsSubmit.clearErrors(fieldName as keyof LoginWithIdentityFormValues);
  };

  const handleBlur = async (fieldName: string) => {
    if (fieldName === "phoneNumber") {
      setIsPhoneNumberFocused(false);
    } else if (fieldName === "identityNo") {
      setIsIdentityNoFocused(false);
    }
    await trigger(fieldName as keyof LoginWithIdentityFormValues);
  };

  const onLoginClick = () => {
    navigate("/");
  };

  return (
    <ContentStyle>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography
            className="!text-[20px] lg:!text-[24px]"
            fontWeight={"700"}
          >
            {getRemoteValue("login_with_identity")}
          </Typography>
        </Box>
      </Stack>
      <Stack>
        <Stack sx={{ height: "100%" }}>
          <FormProvider
            methods={methodsSubmit}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Stack sx={{ height: "100%" }}>
              <Stack sx={{ height: "100%", gap: "4px", marginBottom: "30px" }}>
                <Typography
                  sx={{
                    color: "#64748B",
                    fontSize: "16px",
                    fontWeight: " 400",
                    fontFamily: "Inter",
                    fontStyle: "normal",
                    lineHeight: " normal",
                  }}
                >
                  {getRemoteValue("login_with_identity_description")}
                </Typography>
                <Stack sx={{ mt: 4, mb: shouldShowIdentityNoError() ? 4 : 2 }} gap={shouldShowPhoneNumberError() ? 3.5 : 1.5}>
                  <RHFPhoneInput
                    name={"phoneNumber"}
                    label={getRemoteValue("your_phone_number")}
                    control={control}
                    errors={errors}
                    onFocus={() => handleFocus("phoneNumber")}
                    onBlur={() => handleBlur("phoneNumber")}
                  />
                  <RHFInput
                    name={"identityNo"}
                    label={getRemoteValue("your_identity_number")}
                    control={control}
                    error={errors.identityNo}
                    maxLength={11}
                    type="text"
                    onFocus={() => handleFocus("identityNo")}
                    onBlur={() => handleBlur("identityNo")}
                  />
                </Stack>

                <CLoadingButton
                  fullWidth
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={isDisabled}
                >
                  {getRemoteValue("send_otp")}
                </CLoadingButton>


                <Stack direction={'row'} justifyContent={'space-between'} >
                  <Stack sx={{ py: 2 }} alignItems={'center'}>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        className="!text-[16px] lg:!text-[18px]"
                        fontWeight={"600"}
                      >
                        {getRemoteValue("communication")}
                      </Typography>
                    </Box>
                    <Stack sx={{ mt: 1 }} direction="row" gap={'8px'}>
                      <Stack sx={{ cursor: "pointer" }} onClick={handleMailClick}>
                        <img
                          className="contact-icon-big"
                          src={"/icons/mail-icon-black.svg"}
                          alt=""
                        />
                      </Stack>
                      <Stack sx={{ cursor: "pointer" }} onClick={handleWhatsAppClick}>
                        <img
                          className="contact-icon-big"
                          src={"/icons/whatsapp-icon-black.svg"}
                          alt=""
                        />
                      </Stack>
                    </Stack>
                  </Stack>


                  <Stack direction={'row'} justifyContent={'end'} className="phone-changed-button">
                    <Box role="button" sx={{ py: 2, pl: 2 }} onClick={onLoginClick}>
                      <Typography>
                        {getRemoteValue("login_with_phone")}
                      </Typography>
                    </Box>
                  </Stack>

                </Stack>


              </Stack>
            </Stack>
          </FormProvider>
        </Stack>

      </Stack>
    </ContentStyle>
  );
};

export default IdentityLogin;
